/**
 * S426.3 Player v2 entry. Loads SDK, then defines + mounts <pulse-app>.
 * Falls back to <pulse-degraded-fallback> if SDK fails.
 *
 * `__PULSE_SDK_URL__` is injected at build time by `tools/scripts/build/bundle-pulse-player.ts`
 * via esbuild `define`. Bundling it as a constant keeps inline scripts out of
 * `index.html` (CSP `script-src 'self'` would block them).
 */

declare const __PULSE_SDK_URL__: string;

// S452.4 — auto-register `<sdk-loader>` so every loading state surfaces a
// consistent, branded indicator. Side-effect import; no runtime cost
// when the element isn't placed in the DOM.
import '@libs-platform-sdk/sdk-loader';

// S486 — per-route manifest swap. iOS Safari's "Add to Home Screen"
// launches the URL declared in the manifest's `start_url`, NOT the URL
// the user was on. With a single shared manifest pointing at /player/,
// a user adding /player/now from the share sheet would still launch
// /player/ on next tap. Fix: swap the `<link rel="manifest">` href to
// `manifest-now.json` (start_url = /player/now) when we boot under the
// /player/now path. Done at the top of main.ts so the swap lands
// before any user interaction with the share sheet.
function swapManifestForCurrentRoute(): void {
  if (typeof document === 'undefined') return;
  const path = window.location.pathname;
  if (!path.endsWith('/now') && !path.endsWith('/now/')) return;
  const link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!link) return;
  // Use the same scope prefix the current request landed under, so dev
  // builds (mounted at /) and prod (/player/) both pick the right file.
  const base = link.getAttribute('href')?.replace(/manifest\.json(?:\?.*)?$/, '') ?? '/player/';
  link.setAttribute('href', `${base}manifest-now.json?v=486c`);
}
swapManifestForCurrentRoute();

// S470.12 — install pagehide / visibilitychange flush so pending claps
// (still inside the 800ms debounce window) survive a tab close or refresh
// via `navigator.sendBeacon`. Without this, a user who clapped and then
// immediately reloaded would lose the clap client-side: the debounced
// fetch was never initiated, so its `keepalive: true` flag had nothing
// to keep alive.
import { installClapsUnloadFlush } from './services/claps';
installClapsUnloadFlush();

// S445.4 — chunk-load failure recovery (extracted to its own module so
// it's unit-testable). See `chunk-load-recovery.ts` for the rationale.
import { installChunkLoadErrorHandler } from './services/chunk-load-recovery';
import { prepareDevServiceWorker } from './services/dev-sw-prep';
import { isCapacitor } from './lib/runtime-env';
import { installSafeAreaInsetSync } from './services/safe-area-insets';

// Before pulse-app paints — iOS can report env(safe-area-inset-*) as 0 on first
// frame; sync + cache last-good values so the shell header stays below the notch.
installSafeAreaInsetSync();

// S476.1 — capture beforeinstallprompt BEFORE app boot. The browser fires
// this event when it decides; if we register inside <pulse-app> setup we
// can miss the event. Pre-boot capture into a global; the banner host
// reads it via __pulseInstallPrompt() and listens for the ready event.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e as BeforeInstallPromptEvent;
  window.dispatchEvent(new CustomEvent('pulse-install-prompt-ready'));
});
(globalThis as { __pulseInstallPrompt?: () => BeforeInstallPromptEvent | null }).__pulseInstallPrompt =
  () => deferredInstallPrompt;

/**
 * S452.4 — boot-time discreet loader (floating pill).
 *
 * Modern UX: no full-screen scrim — the empty <pulse-app> shell stays visible
 * centered on screen (no scrim). Shows immediately (delay-ms=0) at xl size.
 * See sdk-loader README § Best practices.
 */
const BOOT_LOADER_ID = 'pulse-boot-loader';
function mountBootLoader(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BOOT_LOADER_ID)) return;
  const el = document.createElement('sdk-loader');
  el.id = BOOT_LOADER_ID;
  el.setAttribute('variant', 'pulse');
  el.setAttribute('pulse-src', '/player/brand/G-Pulse-symbol-animated-v5.svg');
  el.setAttribute('size', 'xl');
  el.setAttribute('mode', 'floating');
  el.setAttribute('anchor', 'center');
  el.setAttribute('tone', 'pulse');
  el.setAttribute('message', 'Loading…');
  el.setAttribute('delay-ms', '0');
  document.body.appendChild(el);
}
function removeBootLoader(): void {
  const el = document.getElementById(BOOT_LOADER_ID);
  if (el) el.remove();
  try {
    (window as unknown as { __pulseBootComplete__?: boolean }).__pulseBootComplete__ = true;
  } catch { /* jsdom */ }
}
// Expose so pulse-app can call it post-first-render.
(window as unknown as { __pulseRemoveBootLoader__?: () => void }).__pulseRemoveBootLoader__ = removeBootLoader;

/**
 * S466.8 — boot-loader watchdog. If pulse-app hasn't removed the boot
 * loader within 25s (hung manifest fetch, stuck SDK script, malformed
 * dynamic import), force-remove it and surface a minimal "something
 * went wrong" card so the user isn't staring at the eternal spinner.
 * 25s tolerates cold deploy + slow LAN; beyond that, show the truth.
 */
const BOOT_LOADER_WATCHDOG_MS = 25_000;
setTimeout(() => {
  if ((window as unknown as { __pulseBootComplete__?: boolean }).__pulseBootComplete__) return;
  if (!document.getElementById(BOOT_LOADER_ID)) return;
  // eslint-disable-next-line no-console
  console.error(
    '[pulse:boot] watchdog fired after %sms — pulse-app never mounted. ' +
      'Likely causes: backend /api/pulse/manifest/* unreachable, SDK script blocked, ' +
      'or a dynamic import that never resolved. Check the Network tab.',
    BOOT_LOADER_WATCHDOG_MS,
  );
  removeBootLoader();
  const card = document.createElement('div');
  card.id = 'pulse-boot-failure';
  card.style.cssText =
    'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;' +
    'background:#0a0a14;color:#eee;font:14px/1.5 system-ui;padding:24px;text-align:center;z-index:9999';
  const inner = document.createElement('div');
  inner.style.maxWidth = '480px';
  const h = document.createElement('h1');
  h.style.cssText = 'font-size:18px;margin:0 0 8px';
  h.textContent = "Player didn't finish loading";
  const p = document.createElement('p');
  p.style.cssText = 'opacity:.7;margin:0 0 16px';
  p.textContent =
    'Check the browser console. Common causes: stale service worker (hard-refresh), ' +
    'backend /api/* unreachable, or a failed chunk import.';
  const btn = document.createElement('button');
  btn.style.cssText =
    'padding:10px 18px;border:0;border-radius:8px;background:#6366f1;color:#fff;font:inherit;cursor:pointer';
  btn.textContent = 'Reload';
  btn.addEventListener('click', () => location.reload());
  inner.append(h, p, btn);
  card.appendChild(inner);
  document.body.appendChild(card);
}, BOOT_LOADER_WATCHDOG_MS);

// S445.12 — dev-only live reload via esbuild SSE + dev-mode SW
// unregister. Both are gated by `process.env.NODE_ENV` so the entire
// import + call get tree-shaken out of production builds.
const IS_DEV = process.env.NODE_ENV !== 'production';

async function loadSdk(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.type = 'module';
    s.src = url.startsWith('./')
      ? new URL(url.slice(2), `${location.origin}/player/`).toString()
      : url;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('SDK failed to load'));
    document.head.appendChild(s);
  });
}

async function bootstrap(): Promise<void> {
  // Dev: drop stale SW *before* loader/chunks — otherwise pulse-app import 404s.
  if (IS_DEV && !(await prepareDevServiceWorker())) return;

  mountBootLoader();

  // S445.4 — install BEFORE any dynamic imports so the first lazy load
  // failure is already covered.
  installChunkLoadErrorHandler();

  // S445.12 — dev-only: unregister any stale SW + subscribe to esbuild's
  // rebuild SSE so the page (including phones via the cloudflared
  // tunnel) auto-reloads on source change. Production path is untouched.
  if (IS_DEV) {
    const { installDevLiveReload } = await import('./services/dev-live-reload');
    installDevLiveReload();
  }

  // Esbuild substitutes the literal at build time. Fallback to a window-global
  // for dev / test environments where esbuild hasn't run.
  const sdkUrl: string = (typeof __PULSE_SDK_URL__ !== 'undefined' && __PULSE_SDK_URL__)
    || (window as unknown as { __PULSE_SDK_URL__?: string }).__PULSE_SDK_URL__
    || '';
  if (!sdkUrl) {
    removeBootLoader();
    const { definePulseDegradedFallback } = await import('./components/pulse-degraded-fallback');
    definePulseDegradedFallback();
    document.body.replaceChildren(document.createElement('pulse-degraded-fallback'));
    return;
  }
  try {
    await loadSdk(sdkUrl);
  } catch {
    removeBootLoader();
    const { definePulseDegradedFallback } = await import('./components/pulse-degraded-fallback');
    definePulseDegradedFallback();
    document.body.replaceChildren(document.createElement('pulse-degraded-fallback'));
    return;
  }
  try {
    await import('./components/pulse-app');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[pulse:boot] pulse-app import failed', err);
    removeBootLoader();
    throw err;
  }
  // S445.12 — register SW only in production. In dev mode the SW
  // caches chunks aggressively and causes "why isn't my change
  // showing up?" surprises. Production behavior unchanged.
  // S476.1 — Capacitor gate per service-worker-and-capacitor-strategy.md.
  // The native WebView's origin (capacitor://) doesn't match same-origin
  // checks and the SW would no-op anyway. Skip registration entirely.
  if (!IS_DEV && 'serviceWorker' in navigator && !isCapacitor()) {
    navigator.serviceWorker.register('/player/sw.js', { scope: '/player/' }).catch(() => { /* non-critical */ });
  }
}

void bootstrap().catch((err) => {
  // eslint-disable-next-line no-console
  console.error('[pulse:boot] bootstrap failed', err);
  removeBootLoader();
});
